import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "./api/client";
import { useAuth } from "./contexts/AuthContext";
import ClaimAccountModal from "./components/ClaimAccountModal";
import PasswordInput from "./components/PasswordInput";

const GRADES = [
  "3","4","4+","5","5+",
  "6A","6A+","6B","6B+","6C","6C+",
  "7A","7A+","7B","7B+","7C","7C+",
  "8A","8A+","8B","8B+","8C","8C+","9A",
];

const SECTOR_PALETTE = [
  "#e8a87c","#7eb5a6","#8b9dc3","#d4726a","#c38d9e",
  "#a8c5a0","#dda15e","#b5a8c9","#5e9fa3","#bc6c25",
];

// Grade sorting map aligned with backend domain model
const GRADE_SORT_MAP = {
  "3": 100, "4": 200, "4+": 250,
  "5": 300, "5+": 350,
  "6A": 400, "6A+": 450, "6B": 500, "6B+": 550,
  "6C": 600, "6C+": 650,
  "7A": 700, "7A+": 750, "7B": 800, "7B+": 850,
  "7C": 900, "7C+": 950,
  "8A": 1000, "8A+": 1050, "8B": 1100, "8B+": 1150,
  "8C": 1200, "8C+": 1250,
  "9A": 1300,
};

// Interest priority constants from domain model
const PRIORITY_WOULD_TRY = 1;
const PRIORITY_KEEN = 2;
const PRIORITY_PROJECT = 3;

function memberColor(name) {
  const hue = name.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
  return `hsl(${hue}, 50%, 65%)`;
}

function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

function isInstagramUrl(url) {
  return url && /instagram\.com\/(p|reel)\//.test(url);
}

function StyleTag({ style, small = false }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: small ? "1px 6px" : "2px 8px", borderRadius: "3px",
      fontSize: small ? "10px" : "11px", fontWeight: 500,
      background: `${style.colour}22`, color: style.colour,
      border: `1px solid ${style.colour}44`,
      letterSpacing: "0.3px", whiteSpace: "nowrap",
    }}>
      {style.name}
    </span>
  );
}

function InterestDot({ name, active }) {
  const color = memberColor(name);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "3px",
      padding: "1px 7px", borderRadius: "10px", fontSize: "10px", fontWeight: 600,
      background: active ? `${color}22` : "transparent",
      color: active ? color : "#444",
      border: `1px solid ${active ? color + "44" : "#333"}`,
      opacity: active ? 1 : 0.5,
    }}>
      {active && "🔥 "}{name}
    </span>
  );
}

const inputStyle = {
  background: "#2a2a2e", border: "1px solid #3a3a3e", borderRadius: "4px",
  padding: "6px 10px", color: "#e8e6e3", fontSize: "13px", outline: "none",
  width: "100%", boxSizing: "border-box",
};

const btnPrimary = {
  padding: "5px 14px", borderRadius: "4px", border: "none", background: "#e8a87c",
  color: "#1a1a1c", fontSize: "12px", fontWeight: 600, cursor: "pointer",
};

const btnSecondary = {
  padding: "5px 14px", borderRadius: "4px", border: "1px solid #3a3a3e",
  background: "transparent", color: "#999", fontSize: "12px", fontWeight: 500, cursor: "pointer",
};

function ClimbCard({ climb, sectors, styles, members, currentUserId, onUpdate, onDelete, onPlayVideo, onRecommend }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState(climb);
  const [videos, setVideos] = useState([]);
  const [videoModal, setVideoModal] = useState(null);
  const sector = sectors.find(s => s.id === climb.sector_id);
  const sectorColor = sector?.colour || "#666";
  const climbStyles = (climb.style_ids || []).map(id => styles.find(s => s.id === id)).filter(Boolean);

  // Load videos when climb changes
  useEffect(() => {
    if (climb.id) {
      api.listClimbVideos(climb.id)
        .then(setVideos)
        .catch(err => console.error('Failed to load videos:', err));
    }
  }, [climb.id]);

  const ytId = getYouTubeId(climb.video_url);
  const isIg = isInstagramUrl(climb.video_url);

  const toggleInterest = async (e, member) => {
    e.stopPropagation();
    const interested = climb.interested || [];
    const isInterested = interested.some(i => i.user_id === member.id);

    try {
      if (isInterested) {
        await api.removeInterest(climb.id, member.id);
      } else {
        await api.createInterest(climb.id, member.id, PRIORITY_KEEN);
      }
      onUpdate();
    } catch (err) {
      console.error('Failed to toggle interest:', err);
    }
  };

  const addVideo = async () => {
    const url = prompt('Enter video URL (YouTube or Instagram):');
    if (!url) return;
    const title = prompt('Enter video title:', 'Beta') || 'Beta';
    try {
      await api.addClimbVideo(climb.id, url, title);
      const updatedVideos = await api.listClimbVideos(climb.id);
      setVideos(updatedVideos);
    } catch (err) {
      console.error('Failed to add video:', err);
      alert('Failed to add video: ' + err.message);
    }
  };

  const editVideo = async (video) => {
    const url = prompt('Enter video URL:', video.url);
    if (!url) return;
    const title = prompt('Enter video title:', video.title) || 'Beta';
    try {
      await api.updateClimbVideo(video.id, url, title);
      const updatedVideos = await api.listClimbVideos(climb.id);
      setVideos(updatedVideos);
    } catch (err) {
      console.error('Failed to update video:', err);
      alert('Failed to update video: ' + err.message);
    }
  };

  const deleteVideo = async (videoId) => {
    if (!confirm('Delete this video?')) return;
    try {
      await api.deleteClimbVideo(videoId);
      const updatedVideos = await api.listClimbVideos(climb.id);
      setVideos(updatedVideos);
    } catch (err) {
      console.error('Failed to delete video:', err);
      alert('Failed to delete video: ' + err.message);
    }
  };

  const saveEdit = async () => {
    try {
      await api.updateClimb(editData.id, {
        name: editData.name,
        grade: editData.grade,
        gradeSort: GRADE_SORT_MAP[editData.grade] || 0,
        notes: editData.notes,
        videoUrl: editData.video_url,
        sectorId: editData.sector_id,
      });
      if (editData.style_ids) {
        await api.setClimbStyles(editData.id, editData.style_ids);
      }
      setEditing(false);
      onUpdate();
    } catch (err) {
      console.error('Failed to update climb:', err);
    }
  };

  if (editing) {
    return (
      <div style={{ background: "#1c1c1e", borderRadius: "6px", padding: "12px", border: "1px solid #3a3a3e" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <input value={editData.name} onChange={e => setEditData({ ...editData, name: e.target.value })}
            placeholder="Problem name" style={inputStyle} />
          <div style={{ display: "flex", gap: "6px" }}>
            <select value={editData.grade} onChange={e => setEditData({ ...editData, grade: e.target.value })}
              style={{ ...inputStyle, flex: 1 }}>
              {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
            <select value={editData.sector_id} onChange={e => setEditData({ ...editData, sector_id: e.target.value })}
              style={{ ...inputStyle, flex: 1 }}>
              {sectors.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <input value={editData.video_url || ""} onChange={e => setEditData({ ...editData, video_url: e.target.value })}
            placeholder="YouTube or Instagram link" style={inputStyle} />
          {styles.length > 0 && (
            <div>
              <div style={{ fontSize: "10px", color: "#888", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Styles</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                {styles.map(s => {
                  const active = (editData.style_ids || []).includes(s.id);
                  return (
                    <span key={s.id} onClick={() => {
                      const ids = active ? editData.style_ids.filter(x => x !== s.id) : [...(editData.style_ids || []), s.id];
                      setEditData({ ...editData, style_ids: ids });
                    }}
                      style={{
                        padding: "2px 8px", borderRadius: "3px", fontSize: "10px", cursor: "pointer",
                        background: active ? `${s.colour}33` : "#2a2a2e",
                        color: active ? s.colour : "#666",
                        border: `1px solid ${active ? s.colour + "66" : "#333"}`,
                      }}>
                      {s.name}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          <textarea value={editData.notes} onChange={e => setEditData({ ...editData, notes: e.target.value })}
            placeholder="Notes, beta, conditions..."
            style={{ ...inputStyle, minHeight: "50px", resize: "vertical", fontFamily: "inherit" }} />
          <div style={{ display: "flex", gap: "6px", justifyContent: "flex-end" }}>
            <button onClick={() => setEditing(false)} style={btnSecondary}>Cancel</button>
            <button onClick={() => onDelete(climb.id)} style={{ ...btnSecondary, color: "#d4726a" }}>Delete</button>
            <button onClick={saveEdit} style={btnPrimary}>Save</button>
          </div>
        </div>
      </div>
    );
  }

  const interestedMembers = climb.interested || [];

  return (
    <div style={{
      background: "#1c1c1e", borderRadius: "6px", padding: "10px 12px",
      border: "1px solid #2a2a2e", borderLeft: `3px solid ${sectorColor}`,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", cursor: "pointer" }}
        onClick={() => setExpanded(!expanded)}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px", flexWrap: "wrap" }}>
            <span style={{
              fontWeight: 700, fontSize: "14px", color: "#e8e6e3",
              fontFamily: "'DM Serif Display', Georgia, serif",
            }}>
              {climb.name}
            </span>
            <span style={{
              fontSize: "11px", fontWeight: 700, color: sectorColor,
              background: `${sectorColor}15`, padding: "1px 6px", borderRadius: "3px",
            }}>
              {climb.grade}
            </span>
            {sector && <span style={{ fontSize: "10px", color: "#555" }}>{sector.name}</span>}
            {interestedMembers.length > 0 && (
              <span style={{ fontSize: "10px", color: "#e8a87c" }}>🔥 {interestedMembers.length}</span>
            )}
            {videos.length > 0 && (
              <span style={{ fontSize: "10px", color: "#d4726a" }}>
                ▶ {videos.length > 1 ? videos.length : ''}
              </span>
            )}
            {videos.length === 0 && ytId && (
              <span onClick={e => { e.stopPropagation(); onPlayVideo(climb.video_url, climb.name, climb.id); }}
                style={{ fontSize: "10px", cursor: "pointer", color: "#d4726a" }} title="Watch beta">
                ▶
              </span>
            )}
            {videos.length === 0 && isIg && <span style={{ fontSize: "10px" }}>📷</span>}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "3px" }}>
            {climbStyles.map(s => <StyleTag key={s.id} style={s} small />)}
          </div>
        </div>
        <div style={{ display: "flex", gap: "6px", alignItems: "center", flexShrink: 0 }}>
          <span onClick={e => { e.stopPropagation(); setEditing(true); setEditData(climb); }}
            style={{ color: "#555", fontSize: "14px", cursor: "pointer", padding: "0 4px" }} title="Edit">✎</span>
          <span style={{ color: "#444", fontSize: "12px" }}>{expanded ? "▾" : "▸"}</span>
        </div>
      </div>

      {expanded && (
        <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #2a2a2e" }}>
          {climb.notes && (
            <p style={{ fontSize: "12px", color: "#999", margin: "0 0 8px", lineHeight: 1.5, fontStyle: "italic" }}>
              {climb.notes}
            </p>
          )}
          {videos.length > 0 && (
            <div style={{ marginBottom: "8px" }}>
              <div style={{ fontSize: "10px", color: "#666", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Beta Videos
              </div>
              {videos.map((video, idx) => {
                const videoYtId = getYouTubeId(video.url);
                const videoIsIg = isInstagramUrl(video.url);
                return (
                  <div key={video.id} style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
                    {videoYtId ? (
                      <div onClick={() => onPlayVideo(video.url, `${climb.name} - ${video.title}`, climb.id)}
                        style={{ fontSize: "11px", color: "#d4726a", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px", flex: 1 }}>
                        ▶ {video.title}
                      </div>
                    ) : videoIsIg ? (
                      <a href={video.url} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: "11px", color: "#d4726a", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "4px", flex: 1 }}>
                        📷 {video.title}
                      </a>
                    ) : (
                      <a href={video.url} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: "11px", color: "#8b9dc3", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "4px", flex: 1 }}>
                        🔗 {video.title}
                      </a>
                    )}
                    <span onClick={(e) => { e.stopPropagation(); editVideo(video); }}
                      style={{ color: "#555", fontSize: "11px", cursor: "pointer", padding: "0 4px" }} title="Edit">✎</span>
                    <span onClick={(e) => { e.stopPropagation(); deleteVideo(video.id); }}
                      style={{ color: "#d4726a", fontSize: "11px", cursor: "pointer", padding: "0 4px" }} title="Delete">×</span>
                  </div>
                );
              })}
              <button onClick={(e) => { e.stopPropagation(); addVideo(); }}
                style={{
                  padding: "2px 8px",
                  fontSize: "10px",
                  background: "#2a2a2e",
                  border: "1px solid #3a3a3e",
                  borderRadius: "4px",
                  color: "#7eb5a6",
                  cursor: "pointer",
                  marginTop: "4px"
                }}>
                + Add Video
              </button>
            </div>
          )}
          {videos.length === 0 && ytId && (
            <div onClick={() => onPlayVideo(climb.video_url, climb.name, climb.id)}
              style={{ fontSize: "11px", color: "#d4726a", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px", marginBottom: "6px" }}>
              ▶ Watch beta video
            </div>
          )}
          {videos.length === 0 && isIg && (
            <a href={climb.video_url} target="_blank" rel="noopener noreferrer"
              style={{ fontSize: "11px", color: "#d4726a", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "4px", marginBottom: "6px" }}>
              📷 View on Instagram
            </a>
          )}
          {videos.length === 0 && climb.video_url && !ytId && !isIg && (
            <div>
              <a href={climb.video_url} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: "11px", color: "#8b9dc3", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "4px", marginBottom: "6px" }}>
                🔗 Video link
              </a>
              <button onClick={(e) => { e.stopPropagation(); addVideo(); }}
                style={{
                  padding: "2px 8px",
                  fontSize: "10px",
                  background: "#2a2a2e",
                  border: "1px solid #3a3a3e",
                  borderRadius: "4px",
                  color: "#7eb5a6",
                  cursor: "pointer",
                  marginLeft: "8px"
                }}>
                + Add Video
              </button>
            </div>
          )}
          {videos.length === 0 && !climb.video_url && (
            <button onClick={(e) => { e.stopPropagation(); addVideo(); }}
              style={{
                padding: "2px 8px",
                fontSize: "10px",
                background: "#2a2a2e",
                border: "1px solid #3a3a3e",
                borderRadius: "4px",
                color: "#7eb5a6",
                cursor: "pointer",
                marginBottom: "6px"
              }}>
              + Add Beta Video
            </button>
          )}
          <div style={{ marginTop: "6px" }}>
            <div style={{ fontSize: "10px", color: "#666", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Who's keen?
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
              {members.filter(m => m && m.name).map(m => {
                const isInterested = interestedMembers.some(i => i.user_id === m.id);
                return (
                  <span key={m.id} onClick={e => toggleInterest(e, m)} style={{ cursor: "pointer" }}>
                    <InterestDot name={m.name} active={isInterested} />
                  </span>
                );
              })}
              {members.length === 0 && <span style={{ fontSize: "10px", color: "#444" }}>Add members first</span>}
            </div>
          </div>
          {members.length > 1 && (
            <div style={{ marginTop: "8px" }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRecommend(climb);
                }}
                style={{
                  padding: "4px 8px",
                  fontSize: "10px",
                  background: "#2a2a2e",
                  border: "1px solid #3a3a3e",
                  borderRadius: "4px",
                  color: "#7eb5a6",
                  cursor: "pointer",
                }}
              >
                💡 Recommend to...
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function VideoPanel({ url, climbName, climbId, userId, onClose, onWatched }) {
  const ytId = getYouTubeId(url);

  const handleClose = async () => {
    // Mark video as watched when closing
    if (climbId && userId) {
      try {
        await api.markVideoWatched(climbId, userId);
        if (onWatched) onWatched(climbId);
      } catch (err) {
        console.error('Failed to mark video as watched:', err);
      }
    }
    onClose();
  };

  if (!ytId) return null;
  return (
    <>
      <div onClick={handleClose} style={{
        position: "fixed",
        inset: 0,
        background: "#000000dd",
        zIndex: 199,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}>
        <div
          onClick={e => e.stopPropagation()}
          style={{
            background: "#1a1a1c",
            borderRadius: "8px",
            border: "1px solid #2a2a2e",
            width: "100%",
            maxWidth: "1200px",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
          }}>
          <div style={{
            padding: "12px 16px",
            borderBottom: "1px solid #2a2a2e",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}>
            <span style={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#e8e6e3",
              fontFamily: "'DM Serif Display', Georgia, serif"
            }}>
              {climbName || "Beta Video"}
            </span>
            <span
              onClick={handleClose}
              style={{
                color: "#666",
                fontSize: "24px",
                cursor: "pointer",
                padding: "0 4px",
                lineHeight: 1,
                transition: "color 0.2s",
              }}
              onMouseEnter={e => e.target.style.color = "#e8e6e3"}
              onMouseLeave={e => e.target.style.color = "#666"}
            >
              ×
            </span>
          </div>
          <div style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            background: "#000",
          }}>
            <div style={{
              width: "100%",
              aspectRatio: "16/9",
              maxHeight: "100%",
            }}>
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${ytId}?autoplay=1`}
                style={{ width: "100%", height: "100%", border: "none" }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Modal({ children, onClose }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "#000000bb",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: "20px",
    }} onClick={onClose}>
      <div style={{
        background: "#1c1c1e", borderRadius: "8px", padding: "20px",
        width: "100%", maxWidth: "420px", border: "1px solid #2a2a2e",
        maxHeight: "90vh", overflowY: "auto", position: "relative",
      }} onClick={e => e.stopPropagation()}>
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            background: "transparent",
            border: "none",
            color: "#666",
            fontSize: "24px",
            cursor: "pointer",
            padding: "0",
            lineHeight: 1,
            width: "24px",
            height: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onMouseEnter={e => e.target.style.color = "#e8e6e3"}
          onMouseLeave={e => e.target.style.color = "#666"}
        >
          ×
        </button>
        {children}
      </div>
    </div>
  );
}

function ModalTitle({ children }) {
  return (
    <h3 style={{ margin: "0 0 12px", fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400, fontSize: "18px" }}>
      {children}
    </h3>
  );
}

export default function TripPlanner() {
  const { user: authUser } = useAuth();
  const { id: tripIdFromRoute } = useParams();
  const [currentUser, setCurrentUser] = useState(null);
  const [currentTrip, setCurrentTrip] = useState(null);
  const [tripName, setTripName] = useState("My Trip");
  const [sectors, setSectors] = useState([]);
  const [sectorColors, setSectorColors] = useState({}); // Store colors client-side
  const [styles, setStyles] = useState([]);
  const [members, setMembers] = useState([]);
  const [climbs, setClimbs] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [watchedClimbs, setWatchedClimbs] = useState(new Set());
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);

  const [filterSector, setFilterSector] = useState("All");
  const [filterStyles, setFilterStyles] = useState([]);
  const [styleFilterMode, setStyleFilterMode] = useState("any"); // "any" or "all"
  const [filterMember, setFilterMember] = useState("All");
  const [filterUnwatched, setFilterUnwatched] = useState(false);
  const [filterGrade, setFilterGrade] = useState("All");
  const [gradeFilterMethod, setGradeFilterMethod] = useState(null); // "gte", "eq", "lte", or null

  const [modal, setModal] = useState(null);
  const [videoState, setVideoState] = useState(null);
  const [duplicateClimb, setDuplicateClimb] = useState(null);

  const [newClimb, setNewClimb] = useState({ name: "", grade: "7A", sector_id: "", style_ids: [], notes: "", video_url: "" });
  const [newSector, setNewSector] = useState({ name: "", colour: SECTOR_PALETTE[0] });
  const [newStyle, setNewStyle] = useState({ name: "", colour: "#e8a87c" });
  const [newMemberName, setNewMemberName] = useState("");
  const [editingTripName, setEditingTripName] = useState(false);
  const [tempTripName, setTempTripName] = useState("");

  // Recommendation state
  const [recommendClimb, setRecommendClimb] = useState(null);
  const [recommendTo, setRecommendTo] = useState("");
  const [recommendReason, setRecommendReason] = useState("");

  // Invite landing page state
  const [inviteLanding, setInviteLanding] = useState(false);
  const [inviteTrip, setInviteTrip] = useState(null);
  const [inviteCode, setInviteCode] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(localStorage.getItem('trip_app_user_id') || "");
  const [newUserName, setNewUserName] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserConfirmPassword, setNewUserConfirmPassword] = useState("");
  const [claimPassword, setClaimPassword] = useState("");
  const [claimConfirmPassword, setClaimConfirmPassword] = useState("");
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [claimingUser, setClaimingUser] = useState(null);

  // Initialize user and trip on mount
  useEffect(() => {
    async function init() {
      try {
        setLoading(true);

        // Check if this is an invite landing page
        const urlPath = window.location.pathname;
        const inviteMatch = urlPath.match(/\/invite\/([a-f0-9-]+)/);
        const code = inviteMatch ? inviteMatch[1] : null;

        // Handle invite code - show landing page (unauthenticated flow)
        if (code) {
          try {
            const trip = await api.getTripByInviteCode(code);
            const members = await api.listTripMembers(trip.id);
            setInviteTrip(trip);
            setInviteCode(code);
            // Extract user info from members - only show demo users (no password set)
            setAllUsers(members
              .filter(m => (m.user_name || m.name) && m.is_demo === true)
              .map(m => ({ id: m.user_id || m.id, name: m.user_name || m.name })));
            setInviteLanding(true);
            setLoading(false);
            return;
          } catch (err) {
            console.error('Failed to load invite:', err);
            alert('Invalid or expired invite link');
            window.history.replaceState({}, '', '/');
          }
        }

        // For authenticated users viewing a trip
        if (authUser) {
          setCurrentUser(authUser);

          // Load the trip from route parameter
          if (tripIdFromRoute) {
            try {
              const trip = await api.getTrip(tripIdFromRoute);
              setCurrentTrip(trip);
              setTripName(trip.name);
              await loadTripData(tripIdFromRoute);
            } catch (err) {
              console.error('Failed to load trip:', err);
              setError('Trip not found');
            }
          }
        }

        // Load area colors from localStorage
        const savedColors = localStorage.getItem('trip_app_sector_colors');
        if (savedColors) {
          setSectorColors(JSON.parse(savedColors));
        }

        setLoaded(true);
      } catch (err) {
        console.error('Failed to initialize:', err);
        setError(err.message);
        // Capture error details if it's an APIError
        if (err.statusCode !== undefined) {
          setErrorDetails({
            type: err.type,
            statusCode: err.statusCode,
            url: err.url,
          });
        }
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [authUser, tripIdFromRoute]);

  const loadTripData = async (tripId) => {
    try {
      // Load all trip data in parallel
      const [sectorsData, stylesData, membersData, climbsData, suggestionsData, watchedData] = await Promise.all([
        api.listSectors(tripId),
        api.listVisibleStylesForTrip(tripId),
        api.listTripMembers(tripId),
        api.listClimbsByTrip(tripId),
        currentUser ? api.listPendingSuggestionsForUserInTrip(currentUser.id, tripId) : Promise.resolve([]),
        currentUser ? api.getWatchedClimbs(currentUser.id, tripId) : Promise.resolve([]),
      ]);

      setSectors(sectorsData || []);
      setStyles(stylesData || []);
      // Normalize member data structure: user_id -> id, user_name -> name
      setMembers((membersData || [])
        .filter(m => (m.user_name || m.name)) // Filter out members without names
        .map(m => ({
          ...m,
          id: m.user_id || m.id,
          name: m.user_name || m.name
        })));
      setSuggestions(suggestionsData || []);
      setWatchedClimbs(new Set((watchedData || []).map(w => w.climb_id)));

      // Load interests for each climb
      const climbsWithInterests = await Promise.all(
        (climbsData || []).map(async (climb) => {
          const interests = await api.listInterestsByClimb(climb.id);
          return { ...climb, interested: interests || [] };
        })
      );

      setClimbs(climbsWithInterests);
    } catch (err) {
      console.error('Failed to load trip data:', err);
      setError(err.message);
      // Capture error details if it's an APIError
      if (err.statusCode !== undefined) {
        setErrorDetails({
          type: err.type,
          statusCode: err.statusCode,
          url: err.url,
        });
      }
    }
  };

  const refreshData = useCallback(async () => {
    if (currentTrip) {
      await loadTripData(currentTrip.id);
    }
  }, [currentTrip]);

  const updateTripName = async (name) => {
    if (!currentTrip) return;
    try {
      await api.updateTrip(currentTrip.id, name);
      setTripName(name);
      setEditingTripName(false);
    } catch (err) {
      console.error('Failed to update trip name:', err);
    }
  };

  const addSector = async () => {
    if (!newSector.name.trim() || !currentTrip) return;
    try {
      // Find next unused color from palette
      const usedColors = Object.values(sectorColors);
      const nextColor = SECTOR_PALETTE.find(c => !usedColors.includes(c)) || SECTOR_PALETTE[sectors.length % SECTOR_PALETTE.length];

      const sector = await api.createSector(currentTrip.id, newSector.name.trim());
      setSectors(prev => [...prev, sector]);

      // Save color client-side (use next unused color)
      const newColors = { ...sectorColors, [sector.id]: nextColor };
      setSectorColors(newColors);
      localStorage.setItem('trip_app_sector_colors', JSON.stringify(newColors));

      setNewSector({ name: "", colour: SECTOR_PALETTE[0] });
    } catch (err) {
      console.error('Failed to add sector:', err);
    }
  };

  const removeSector = async (id) => {
    try {
      await api.deleteSector(id);
      setSectors(prev => prev.filter(a => a.id !== id));
      setClimbs(prev => prev.filter(c => c.sector_id !== id));

      // Remove color
      const newColors = { ...sectorColors };
      delete newColors[id];
      setSectorColors(newColors);
      localStorage.setItem('trip_app_sector_colors', JSON.stringify(newColors));
    } catch (err) {
      console.error('Failed to remove area:', err);
    }
  };

  const addStyleFn = async () => {
    if (!newStyle.name.trim() || !currentUser) return;
    try {
      const style = await api.createStyle(currentUser.id, newStyle.name.trim(), newStyle.colour);
      setStyles(prev => [...prev, style]);
      setNewStyle({ name: "", colour: "#e8a87c" });
    } catch (err) {
      console.error('Failed to add style:', err);
    }
  };

  const removeStyle = async (id) => {
    if (!currentUser) return;
    try {
      await api.deleteStyle(id, currentUser.id);
      setStyles(prev => prev.filter(s => s.id !== id));
      setClimbs(prev => prev.map(c => ({ ...c, style_ids: (c.style_ids || []).filter(sid => sid !== id) })));
    } catch (err) {
      console.error('Failed to remove style:', err);
    }
  };

  const addMemberFn = async () => {
    if (!newMemberName.trim() || !currentTrip) return;
    try {
      // Create user
      const newUser = await api.createUser(newMemberName.trim());
      // Add to trip
      await api.joinTrip(currentTrip.id, newUser.id);
      // Refresh members
      const updatedMembers = await api.listTripMembers(currentTrip.id);
      setMembers(updatedMembers
        .filter(m => (m.user_name || m.name)) // Filter out members without names
        .map(m => ({
          ...m,
          id: m.user_id || m.id,
          name: m.user_name || m.name
        })));
      setNewMemberName("");
    } catch (err) {
      console.error('Failed to add member:', err);
    }
  };

  const leaveTrip = async () => {
    if (!currentTrip || !currentUser) return;

    if (!confirm(`Are you sure you want to leave "${currentTrip.name}"? You can rejoin using the invite link.`)) {
      return;
    }

    try {
      // Note: When leaving, user removes themselves, so userId == requestingUserId
      await api.removeTripMember(currentTrip.id, currentUser.id, currentUser.id);

      // Clear current trip and redirect to home
      localStorage.removeItem('trip_app_trip_id');
      setCurrentTrip(null);
      setModal(null);

      // Create a new trip
      const trip = await api.createTrip("My Trip", currentUser.id);
      localStorage.setItem('trip_app_trip_id', trip.id);
      setCurrentTrip(trip);
      setTripName(trip.name);
      window.history.replaceState({}, '', `/trip/${trip.id}`);

      // Reload trip data
      await loadTripData(trip.id);
    } catch (err) {
      console.error('Failed to leave trip:', err);
      alert('Failed to leave trip: ' + err.message);
    }
  };

  const saveTripName = async () => {
    if (!currentTrip || !currentUser || !tempTripName.trim()) return;

    try {
      await api.updateTrip(currentTrip.id, tempTripName.trim(), currentUser.id);
      setTripName(tempTripName.trim());
      setEditingTripName(false);
      // Update currentTrip to reflect new name
      setCurrentTrip({ ...currentTrip, name: tempTripName.trim() });
    } catch (err) {
      console.error('Failed to update trip name:', err);
      alert('Failed to update trip name: ' + err.message);
      setTempTripName(tripName); // Revert
    }
  };

  const removeMemberAsAdmin = async (memberIdToRemove) => {
    if (!currentTrip || !currentUser) return;

    const member = members.find(m => m.id === memberIdToRemove);
    if (!member) return;

    if (!confirm(`Remove ${member.name} from the trip? This will delete all their interests and suggestions.`)) {
      return;
    }

    try {
      await api.removeTripMember(currentTrip.id, memberIdToRemove, currentUser.id);
      await refreshData(); // Reload all trip data
    } catch (err) {
      console.error('Failed to remove member:', err);
      if (err.statusCode === 403) {
        alert('Only the trip creator can remove members.');
      } else {
        alert('Failed to remove member: ' + err.message);
      }
    }
  };

  const deleteTrip = async () => {
    if (!currentTrip || !currentUser) return;

    const confirmText = `Are you absolutely sure you want to DELETE "${currentTrip.name}"?\n\n` +
      `This will permanently delete:\n` +
      `• All ${climbs.length} climbs\n` +
      `• All ${sectors.length} sectors\n` +
      `• All member data (${members.length} members)\n` +
      `• All interests and suggestions\n\n` +
      `THIS CANNOT BE UNDONE.\n\n` +
      `Type the trip name to confirm: "${currentTrip.name}"`;

    const userInput = prompt(confirmText);

    if (userInput !== currentTrip.name) {
      if (userInput !== null) { // null means cancelled
        alert('Trip name did not match. Deletion cancelled.');
      }
      return;
    }

    try {
      await api.deleteTrip(currentTrip.id, currentUser.id);

      // Clear current trip and create new one
      localStorage.removeItem('trip_app_trip_id');
      setCurrentTrip(null);
      setModal(null);

      // Create a new trip
      const trip = await api.createTrip("My Trip", currentUser.id);
      localStorage.setItem('trip_app_trip_id', trip.id);
      setCurrentTrip(trip);
      setTripName(trip.name);
      window.history.replaceState({}, '', `/trip/${trip.id}`);

      // Reload trip data
      await loadTripData(trip.id);

      alert('Trip deleted successfully. Created a new trip for you.');
    } catch (err) {
      console.error('Failed to delete trip:', err);
      if (err.statusCode === 403) {
        alert('Only the trip creator can delete the trip.');
      } else {
        alert('Failed to delete trip: ' + err.message);
      }
    }
  };

  const checkForDuplicateClimb = () => {
    if (!newClimb.name.trim()) return null;

    const nameMatch = climbs.find(c =>
      c.name.toLowerCase().trim() === newClimb.name.toLowerCase().trim()
    );

    if (nameMatch) return { climb: nameMatch, matchType: 'name' };

    // Check for YouTube URL match if a video URL is provided
    if (newClimb.video_url) {
      const newVideoId = getYouTubeId(newClimb.video_url);
      if (newVideoId) {
        const videoMatch = climbs.find(c => {
          const existingVideoId = getYouTubeId(c.video_url);
          return existingVideoId && existingVideoId === newVideoId;
        });
        if (videoMatch) return { climb: videoMatch, matchType: 'video' };
      }
    }

    return null;
  };

  const createClimbAndAddInterest = async () => {
    try {
      const climb = await api.createClimb(newClimb.sector_id, {
        name: newClimb.name,
        grade: newClimb.grade,
        gradeSort: GRADE_SORT_MAP[newClimb.grade] || 0,
        notes: newClimb.notes,
        videoUrl: newClimb.video_url,
        createdBy: currentUser.id,
      });

      if (newClimb.style_ids && newClimb.style_ids.length > 0) {
        await api.setClimbStyles(climb.id, newClimb.style_ids);
      }

      // Automatically add interest for the user who created the climb
      await api.createInterest(climb.id, currentUser.id, PRIORITY_KEEN);

      await refreshData();
      setNewClimb({ name: "", grade: "7A", sector_id: sectors[0]?.id || "", style_ids: [], notes: "", video_url: "" });
      setModal(null);
      setDuplicateClimb(null);
    } catch (err) {
      console.error('Failed to add climb:', err);
    }
  };

  const addClimbFn = async () => {
    if (!newClimb.name.trim() || !newClimb.sector_id || !currentUser) return;

    // Check for duplicates
    const duplicate = checkForDuplicateClimb();
    if (duplicate) {
      setDuplicateClimb(duplicate);
      return;
    }

    await createClimbAndAddInterest();
  };

  const handleAddToExisting = async () => {
    if (!duplicateClimb || !currentUser) return;

    try {
      // Add interest to existing climb
      await api.createInterest(duplicateClimb.climb.id, currentUser.id, PRIORITY_KEEN);

      await refreshData();
      setNewClimb({ name: "", grade: "7A", sector_id: sectors[0]?.id || "", style_ids: [], notes: "", video_url: "" });
      setModal(null);
      setDuplicateClimb(null);
    } catch (err) {
      console.error('Failed to add interest to existing climb:', err);
    }
  };

  const handleCreateAnyway = async () => {
    setDuplicateClimb(null);
    await createClimbAndAddInterest();
  };

  const deleteClimb = async (id) => {
    try {
      await api.deleteClimb(id);
      setClimbs(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      console.error('Failed to delete climb:', err);
    }
  };

  const resetData = async () => {
    if (!confirm('Are you sure you want to reset all data? This will delete the current trip.')) return;

    try {
      if (currentTrip) {
        await api.deleteTrip(currentTrip.id);
      }

      localStorage.removeItem('trip_app_trip_id');
      localStorage.removeItem('trip_app_sector_colors');

      // Create new trip
      if (currentUser) {
        const trip = await api.createTrip("My Trip", currentUser.id);
        localStorage.setItem('trip_app_trip_id', trip.id);
        setCurrentTrip(trip);
        setTripName(trip.name);
      }

      setSectors([]);
      setStyles([]);
      setMembers([]);
      setClimbs([]);
      setSectorColors({});
      setFilterSector("All");
      setFilterStyles([]);
      setFilterMember("All");
      setFilterGrade("All");
      setGradeFilterMethod(null);
    } catch (err) {
      console.error('Failed to reset data:', err);
    }
  };

  const handleJoinTrip = async () => {
    try {
      setLoading(true);

      if (selectedUserId) {
        // Validate password
        if (claimPassword.length < 8) {
          alert('Password must be at least 8 characters');
          setLoading(false);
          return;
        }

        if (claimPassword !== claimConfirmPassword) {
          alert('Passwords do not match');
          setLoading(false);
          return;
        }

        // Claim the account with password (only demo users are shown in dropdown)
        try {
          await api.claimAccount(selectedUserId, claimPassword);

          // Join the trip
          await api.joinTrip(inviteTrip.id, selectedUserId);

          // Redirect to trip
          window.location.href = `/trip/${inviteTrip.id}`;
          return;
        } catch (err) {
          if (err.statusCode === 400 || err.message?.includes('password')) {
            alert('Failed to claim account: ' + err.message);
          } else {
            throw err; // Re-throw to be caught by outer catch
          }
          setLoading(false);
          return;
        }
      } else if (newUserName.trim()) {
        // Create new user with password (registration)
        if (newUserPassword.length < 8) {
          alert('Password must be at least 8 characters');
          setLoading(false);
          return;
        }

        if (newUserPassword !== newUserConfirmPassword) {
          alert('Passwords do not match');
          setLoading(false);
          return;
        }

        // Register new user (creates authenticated account)
        try {
          const userData = await api.register(newUserName.trim(), newUserPassword);

          // Join the trip
          await api.joinTrip(inviteTrip.id, userData.user.id);

          // Redirect to trip
          window.location.href = `/trip/${inviteTrip.id}`;
          return;
        } catch (err) {
          if (err.statusCode === 409) {
            alert('Name already taken. Please choose a different name or select your existing account.');
          } else {
            alert(err.message || 'Failed to create account');
          }
          setLoading(false);
          return;
        }
      } else {
        alert('Please select a user or enter a name and password');
        setLoading(false);
        return;
      }
    } catch (err) {
      console.error('Error joining trip:', err);
      alert(err.message || 'Failed to join trip');
      setLoading(false);
    }
  };

  const handleClaimSuccess = async () => {
    try {
      // After claiming, join the trip
      await api.joinTrip(inviteTrip.id, claimingUser.id);

      // Redirect to trip
      window.location.href = `/trip/${inviteTrip.id}`;
    } catch (err) {
      console.error('Error joining trip after claim:', err);
      alert(err.message || 'Failed to join trip');
      setShowClaimModal(false);
      setLoading(false);
    }
  };

  const handleRecommend = async () => {
    if (!recommendTo || !recommendClimb || !currentUser) return;

    try {
      await api.createSuggestion(recommendClimb.id, currentUser.id, recommendTo, recommendReason);
      setModal(null);
      setRecommendClimb(null);
      setRecommendTo("");
      setRecommendReason("");
      alert('Recommendation sent!');
      // Refresh data to update suggestions for all users
      await refreshData();
    } catch (err) {
      console.error('Failed to create recommendation:', err);
      alert('Failed to send recommendation: ' + err.message);
    }
  };

  const handleAcceptSuggestion = async (suggestion) => {
    try {
      await api.acceptSuggestion(suggestion.id);
      // Refresh suggestions
      if (currentTrip && currentUser) {
        const suggestionsData = await api.listPendingSuggestionsForUserInTrip(currentUser.id, currentTrip.id);
        setSuggestions(suggestionsData || []);
        // Refresh interests
        await refreshData();
      }
    } catch (err) {
      console.error('Failed to accept suggestion:', err);
      alert('Failed to accept recommendation: ' + err.message);
    }
  };

  const handleDismissSuggestion = async (suggestion) => {
    try {
      await api.dismissSuggestion(suggestion.id);
      // Refresh suggestions
      if (currentTrip && currentUser) {
        const suggestionsData = await api.listPendingSuggestionsForUserInTrip(currentUser.id, currentTrip.id);
        setSuggestions(suggestionsData || []);
      }
    } catch (err) {
      console.error('Failed to dismiss suggestion:', err);
      alert('Failed to dismiss recommendation: ' + err.message);
    }
  };

  // Add colors to sectors with proper fallback
  const sectorsWithColors = sectors.map((sector, index) => ({
    ...sector,
    colour: sectorColors[sector.id] || SECTOR_PALETTE[index % SECTOR_PALETTE.length],
  }));

  const filtered = climbs.filter(c => {
    if (filterSector !== "All" && c.sector_id !== filterSector) return false;
    if (filterStyles.length > 0) {
      const climbStyleIds = c.style_ids || [];
      if (styleFilterMode === "any") {
        // Show climbs that have ANY of the selected styles
        if (!filterStyles.some(s => climbStyleIds.includes(s))) return false;
      } else {
        // Show climbs that have ALL of the selected styles
        if (!filterStyles.every(s => climbStyleIds.includes(s))) return false;
      }
    }
    if (filterMember !== "All") {
      const memberInterested = c.interested?.some(i => i.user_id === filterMember);
      if (!memberInterested) return false;
    }
    if (filterUnwatched && c.video_url) {
      // Only show climbs with videos that haven't been watched
      if (watchedClimbs.has(c.id)) return false;
    }
    if (filterGrade !== "All" && gradeFilterMethod) {
      const climbGradeIndex = GRADES.indexOf(c.grade);
      const filterGradeIndex = GRADES.indexOf(filterGrade);
      if (gradeFilterMethod === "gte" && climbGradeIndex < filterGradeIndex) return false;
      if (gradeFilterMethod === "eq" && climbGradeIndex !== filterGradeIndex) return false;
      if (gradeFilterMethod === "lte" && climbGradeIndex > filterGradeIndex) return false;
    }
    return true;
  });

  const sortedFiltered = [...filtered].sort((a, b) => {
    const ga = GRADES.indexOf(a.grade), gb = GRADES.indexOf(b.grade);
    return ga !== gb ? ga - gb : a.name.localeCompare(b.name);
  });

  const totalInterested = climbs.reduce((acc, c) => acc + (c.interested?.length || 0), 0);
  const isEmpty = sectors.length === 0 && climbs.length === 0 && members.length === 0;

  if (loading) {
    return (
      <div style={{
        minHeight: "100vh", background: "#111113", color: "#e8e6e3",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "18px", marginBottom: "8px" }}>Loading...</div>
          <div style={{ fontSize: "12px", color: "#666" }}>Connecting to server</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: "100vh", background: "#111113", color: "#e8e6e3",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
      }}>
        <div style={{ textAlign: "center", maxWidth: "500px", padding: "20px" }}>
          <div style={{ fontSize: "18px", color: "#d4726a", marginBottom: "12px" }}>
            {errorDetails?.type === 'NETWORK_ERROR' ? 'Network Error' :
             errorDetails?.type === 'HTTP_ERROR' ? `Server Error (${errorDetails.statusCode})` :
             'Connection Error'}
          </div>
          <div style={{ fontSize: "13px", color: "#e8e6e3", marginBottom: "16px", lineHeight: "1.5" }}>
            {error}
          </div>
          {errorDetails && (
            <div style={{
              background: "#1c1c1e",
              border: "1px solid #2a2a2e",
              borderRadius: "6px",
              padding: "12px",
              marginBottom: "16px",
              textAlign: "left",
            }}>
              <div style={{ fontSize: "10px", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px" }}>
                Technical Details
              </div>
              {errorDetails.type === 'NETWORK_ERROR' && (
                <div style={{ fontSize: "12px", color: "#999" }}>
                  • Unable to connect to {errorDetails.url}<br/>
                  • Check your internet connection<br/>
                  • Verify the API server is running
                </div>
              )}
              {errorDetails.type === 'HTTP_ERROR' && (
                <div style={{ fontSize: "12px", color: "#999" }}>
                  • Status: {errorDetails.statusCode}<br/>
                  • URL: {errorDetails.url}<br/>
                  • The server returned an error response
                </div>
              )}
              {errorDetails.type === 'UNKNOWN_ERROR' && (
                <div style={{ fontSize: "12px", color: "#999" }}>
                  • URL: {errorDetails.url}<br/>
                  • An unexpected error occurred
                </div>
              )}
            </div>
          )}
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "8px 16px",
              background: "#e8a87c",
              border: "none",
              borderRadius: "4px",
              color: "#1a1a1c",
              fontSize: "12px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (inviteLanding && inviteTrip) {
    return (
      <div style={{
        minHeight: "100vh", background: "#111113", color: "#e8e6e3",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
        padding: "20px",
      }}>
        <div style={{
          maxWidth: "500px", width: "100%",
          background: "#1c1c1e", borderRadius: "12px", padding: "32px",
          border: "1px solid #3a3a3e",
        }}>
          <h2 style={{
            fontFamily: "'DM Serif Display', Georgia, serif",
            fontSize: "24px", fontWeight: 400, margin: "0 0 8px",
          }}>
            Join Trip
          </h2>
          <p style={{ color: "#999", fontSize: "14px", margin: "0 0 24px" }}>
            You've been invited to join <strong style={{ color: "#e8e6e3" }}>{inviteTrip.name}</strong>
          </p>

          {allUsers.length > 0 && (
            <>
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", fontSize: "13px", marginBottom: "8px", color: "#ccc" }}>
                  Claim existing account
                </label>
                <select
                  value={selectedUserId}
                  onChange={e => {
                    setSelectedUserId(e.target.value);
                    if (e.target.value) {
                      setNewUserName("");
                      setClaimPassword("");
                      setClaimConfirmPassword("");
                    }
                  }}
                  style={{
                    width: "100%", padding: "10px 12px", background: "#2a2a2e",
                    border: "1px solid #3a3a3e", borderRadius: "6px", color: "#e8e6e3",
                    fontSize: "14px", fontFamily: "inherit",
                  }}
                >
                  <option value="">-- Select a user --</option>
                  {allUsers
                    .filter(user => user && user.name)
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map(user => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    ))}
                </select>
              </div>

              {selectedUserId && (
                <div style={{ marginBottom: "24px" }}>
                  <PasswordInput
                    password={claimPassword}
                    confirmPassword={claimConfirmPassword}
                    onPasswordChange={setClaimPassword}
                    onConfirmPasswordChange={setClaimConfirmPassword}
                  />
                </div>
              )}

              <div style={{
                textAlign: "center", color: "#666", fontSize: "12px",
                margin: "16px 0", textTransform: "uppercase", letterSpacing: "0.5px",
              }}>
                or
              </div>
            </>
          )}

          <div style={{ marginBottom: "24px" }}>
            <label style={{ display: "block", fontSize: "13px", marginBottom: "8px", color: "#ccc" }}>
              Create new account
            </label>
            <input
              type="text"
              placeholder="Enter your name"
              value={newUserName}
              onChange={e => {
                setNewUserName(e.target.value);
                if (e.target.value.trim()) {
                  setSelectedUserId("");
                  setClaimPassword("");
                  setClaimConfirmPassword("");
                }
              }}
              style={{
                width: "100%", padding: "10px 12px", background: "#2a2a2e",
                border: "1px solid #3a3a3e", borderRadius: "6px", color: "#e8e6e3",
                fontSize: "14px", fontFamily: "inherit", marginBottom: "12px",
              }}
            />

            {newUserName.trim() && (
              <div style={{ marginTop: "12px" }}>
                <PasswordInput
                  password={newUserPassword}
                  confirmPassword={newUserConfirmPassword}
                  onPasswordChange={setNewUserPassword}
                  onConfirmPasswordChange={setNewUserConfirmPassword}
                />
              </div>
            )}
          </div>

          <button
            onClick={handleJoinTrip}
            disabled={
              (selectedUserId && (claimPassword.length < 8 || claimPassword !== claimConfirmPassword)) ||
              (!selectedUserId && (!newUserName.trim() || newUserPassword.length < 8 || newUserPassword !== newUserConfirmPassword))
            }
            style={{
              width: "100%", padding: "12px", background: "#e8a87c",
              border: "none", borderRadius: "6px", color: "#1a1a1c",
              fontSize: "14px", fontWeight: 600, cursor: "pointer",
              opacity: (
                (selectedUserId && (claimPassword.length < 8 || claimPassword !== claimConfirmPassword)) ||
                (!selectedUserId && (!newUserName.trim() || newUserPassword.length < 8 || newUserPassword !== newUserConfirmPassword))
              ) ? 0.5 : 1,
            }}
          >
            {selectedUserId ? 'Claim Account & Join' : 'Create Account & Join'}
          </button>
        </div>

        {showClaimModal && claimingUser && (
          <ClaimAccountModal
            userName={claimingUser.name}
            userId={claimingUser.id}
            onSuccess={handleClaimSuccess}
            onCancel={() => {
              setShowClaimModal(false);
              setClaimingUser(null);
              setLoading(false);
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh", background: "#111113", color: "#e8e6e3",
      fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Serif+Display&display=swap" rel="stylesheet" />

      <div style={{
        padding: "20px 20px 16px", borderBottom: "1px solid #2a2a2e",
        background: "linear-gradient(180deg, #1a1a1c 0%, #111113 100%)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
          <div>
            <button
              onClick={() => window.location.href = '/trips'}
              style={{
                ...btnSecondary,
                fontSize: "11px",
                marginBottom: "8px",
                padding: "4px 10px",
              }}
            >
              ← All Trips
            </button>
            {editingTripName ? (
              <input value={tripName}
                onChange={e => setTripName(e.target.value)}
                onBlur={() => updateTripName(tripName)}
                onKeyDown={e => e.key === "Enter" && updateTripName(tripName)}
                autoFocus
                style={{ ...inputStyle, fontSize: "22px", fontFamily: "'DM Serif Display', Georgia, serif",
                  fontWeight: 400, background: "transparent", border: "1px solid #3a3a3e",
                  padding: "2px 8px", width: "auto" }} />
            ) : (
              <h1 onClick={() => setEditingTripName(true)}
                style={{
                  margin: 0, fontSize: "22px", cursor: "pointer",
                  fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400,
                  background: "linear-gradient(135deg, #e8a87c, #d4726a)",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                }}>
                {tripName}
              </h1>
            )}
            <p style={{ margin: "2px 0 0", fontSize: "11px", color: "#555", letterSpacing: "0.3px" }}>
              {climbs.length} problem{climbs.length !== 1 ? "s" : ""} · {members.length} climber{members.length !== 1 ? "s" : ""} · {totalInterested} 🔥
            </p>
          </div>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <button onClick={() => setModal("addSector")} style={btnSecondary}>+ Sector</button>
            <button onClick={() => setModal("member")} style={btnSecondary}>+ Person</button>
            <button onClick={() => setModal("styles")} style={btnSecondary}>+ Style</button>
            {currentTrip && (
              <button onClick={() => setModal("share")} style={{ ...btnSecondary, fontSize: "11px" }}>
                🔗 Share
              </button>
            )}
            <button onClick={() => setModal("queue")} style={{ ...btnSecondary, fontSize: "11px", position: "relative" }}>
              📬 Queue
              {suggestions.length > 0 && (
                <span style={{
                  position: "absolute", top: "-4px", right: "-4px",
                  background: "#d4726a", color: "#fff",
                  borderRadius: "10px", padding: "2px 6px",
                  fontSize: "9px", fontWeight: 600,
                }}>
                  {suggestions.length}
                </span>
              )}
            </button>
            <button onClick={() => setModal("settings")} style={{ ...btnSecondary, fontSize: "11px" }}>
              ⚙️
            </button>
          </div>
        </div>

        {members.length > 0 && (
          <div style={{ padding: "0 20px 12px", display: "flex", gap: "4px", flexWrap: "wrap" }}>
            {members.filter(m => m.name).map(m => {
              const color = memberColor(m.name);
              return (
                <span key={m.id} style={{
                  display: "inline-flex", alignItems: "center", gap: "4px",
                  padding: "2px 8px", borderRadius: "10px", fontSize: "10px", fontWeight: 600,
                  background: `${color}15`, color, border: `1px solid ${color}33`,
                }}>
                  {m.name}
                </span>
              );
            })}
          </div>
        )}

        {/* Filter Panel */}
        {(styles.length > 0 || members.length > 0) && (
          <div style={{
            padding: "12px 20px",
            background: "#1a1a1c",
            borderTop: "1px solid #2a2a2e",
            borderBottom: "1px solid #2a2a2e",
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "8px",
            }}>
              <span style={{
                fontSize: "10px",
                color: "#666",
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                fontWeight: 600,
              }}>
                🔍 Filters
              </span>
              <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <button
                  onClick={() => {
                    console.log('Toggle filter mode clicked, current:', styleFilterMode);
                    setStyleFilterMode(prev => prev === "any" ? "all" : "any");
                  }}
                  disabled={filterStyles.length === 0}
                  style={{
                    padding: "2px 8px",
                    fontSize: "9px",
                    background: styleFilterMode === "all" ? "#7eb5a633" : "#2a2a2e",
                    border: `1px solid ${styleFilterMode === "all" ? "#7eb5a655" : "#3a3a3e"}`,
                    borderRadius: "3px",
                    color: styleFilterMode === "all" ? "#7eb5a6" : "#666",
                    cursor: filterStyles.length === 0 ? "not-allowed" : "pointer",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    fontWeight: 500,
                    transition: "all 0.15s",
                    opacity: filterStyles.length === 0 ? 0.3 : 1,
                  }}
                >
                  Match: {styleFilterMode === "any" ? "Any" : "Specific"}
                </button>
                <button
                  onClick={() => {
                    setFilterStyles([]);
                    setStyleFilterMode("any");
                    setFilterMember("All");
                    setFilterUnwatched(false);
                    setFilterGrade("All");
                    setGradeFilterMethod(null);
                  }}
                  disabled={filterStyles.length === 0 && filterMember === "All" && !filterUnwatched && filterGrade === "All"}
                  style={{
                    padding: "2px 8px",
                    fontSize: "10px",
                    background: "transparent",
                    border: "1px solid #3a3a3e",
                    borderRadius: "3px",
                    color: "#d4726a",
                    cursor: (filterStyles.length === 0 && filterMember === "All" && !filterUnwatched && filterGrade === "All") ? "not-allowed" : "pointer",
                    opacity: (filterStyles.length === 0 && filterMember === "All" && !filterUnwatched && filterGrade === "All") ? 0.3 : 1,
                  }}
                >
                  Clear all
                </button>
              </div>
            </div>

            {/* Style Filters */}
            {styles.length > 0 && (
              <div style={{ marginBottom: "8px" }}>
                <div style={{ fontSize: "9px", color: "#555", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Styles
                </div>
                <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                  {styles.map(s => (
                    <span
                      key={s.id}
                      onClick={() => setFilterStyles(prev =>
                        prev.includes(s.id) ? prev.filter(x => x !== s.id) : [...prev, s.id]
                      )}
                      style={{
                        padding: "3px 10px",
                        borderRadius: "4px",
                        fontSize: "11px",
                        cursor: "pointer",
                        background: filterStyles.includes(s.id) ? `${s.colour}33` : "#2a2a2e",
                        color: filterStyles.includes(s.id) ? s.colour : "#666",
                        border: `1px solid ${filterStyles.includes(s.id) ? s.colour + "55" : "#333"}`,
                        fontWeight: filterStyles.includes(s.id) ? 600 : 400,
                        transition: "all 0.15s",
                      }}
                    >
                      {s.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Grade Filter */}
            <div style={{ marginBottom: "8px" }}>
              <div style={{ fontSize: "9px", color: "#555", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Grade
              </div>
              <div style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
                <select
                  value={filterGrade}
                  onChange={(e) => setFilterGrade(e.target.value)}
                  style={{
                    padding: "4px 8px",
                    fontSize: "11px",
                    background: "#2a2a2e",
                    border: "1px solid #3a3a3e",
                    borderRadius: "4px",
                    color: "#e8e6e3",
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  <option value="All">All Grades</option>
                  {GRADES.map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
                <div style={{ display: "flex", gap: "4px" }}>
                  <button
                    onClick={() => setGradeFilterMethod(gradeFilterMethod === "gte" ? null : "gte")}
                    disabled={filterGrade === "All"}
                    style={{
                      padding: "4px 10px",
                      fontSize: "11px",
                      background: gradeFilterMethod === "gte" ? "#7eb5a6" : "#2a2a2e",
                      border: `2px solid ${gradeFilterMethod === "gte" ? "#7eb5a6" : "#3a3a3e"}`,
                      borderRadius: "4px",
                      color: gradeFilterMethod === "gte" ? "#0a0a0c" : "#666",
                      cursor: filterGrade === "All" ? "not-allowed" : "pointer",
                      fontWeight: gradeFilterMethod === "gte" ? 700 : 500,
                      transition: "all 0.15s",
                      opacity: filterGrade === "All" ? 0.3 : 1,
                    }}
                  >
                    ≥
                  </button>
                  <button
                    onClick={() => setGradeFilterMethod(gradeFilterMethod === "eq" ? null : "eq")}
                    disabled={filterGrade === "All"}
                    style={{
                      padding: "4px 10px",
                      fontSize: "11px",
                      background: gradeFilterMethod === "eq" ? "#7eb5a6" : "#2a2a2e",
                      border: `2px solid ${gradeFilterMethod === "eq" ? "#7eb5a6" : "#3a3a3e"}`,
                      borderRadius: "4px",
                      color: gradeFilterMethod === "eq" ? "#0a0a0c" : "#666",
                      cursor: filterGrade === "All" ? "not-allowed" : "pointer",
                      fontWeight: gradeFilterMethod === "eq" ? 700 : 500,
                      transition: "all 0.15s",
                      opacity: filterGrade === "All" ? 0.3 : 1,
                    }}
                  >
                    =
                  </button>
                  <button
                    onClick={() => setGradeFilterMethod(gradeFilterMethod === "lte" ? null : "lte")}
                    disabled={filterGrade === "All"}
                    style={{
                      padding: "4px 10px",
                      fontSize: "11px",
                      background: gradeFilterMethod === "lte" ? "#7eb5a6" : "#2a2a2e",
                      border: `2px solid ${gradeFilterMethod === "lte" ? "#7eb5a6" : "#3a3a3e"}`,
                      borderRadius: "4px",
                      color: gradeFilterMethod === "lte" ? "#0a0a0c" : "#666",
                      cursor: filterGrade === "All" ? "not-allowed" : "pointer",
                      fontWeight: gradeFilterMethod === "lte" ? 700 : 500,
                      transition: "all 0.15s",
                      opacity: filterGrade === "All" ? 0.3 : 1,
                    }}
                  >
                    ≤
                  </button>
                </div>
              </div>
            </div>

            {/* Member Interest Filter */}
            {members.length > 0 && (
              <div>
                <div style={{ fontSize: "9px", color: "#555", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Interested climber
                </div>
                <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                  <span
                    onClick={() => setFilterMember("All")}
                    style={{
                      padding: "3px 10px",
                      borderRadius: "4px",
                      fontSize: "11px",
                      cursor: "pointer",
                      background: filterMember === "All" ? "#e8a87c33" : "#2a2a2e",
                      color: filterMember === "All" ? "#e8a87c" : "#666",
                      border: `1px solid ${filterMember === "All" ? "#e8a87c55" : "#333"}`,
                      fontWeight: filterMember === "All" ? 600 : 400,
                      transition: "all 0.15s",
                    }}
                  >
                    All
                  </span>
                  {members.filter(m => m.name).map(m => {
                    const color = memberColor(m.name);
                    const isActive = filterMember === m.id;
                    return (
                      <span
                        key={m.id}
                        onClick={() => setFilterMember(isActive ? "All" : m.id)}
                        style={{
                          padding: "3px 10px",
                          borderRadius: "4px",
                          fontSize: "11px",
                          cursor: "pointer",
                          background: isActive ? `${color}33` : "#2a2a2e",
                          color: isActive ? color : "#666",
                          border: `1px solid ${isActive ? color + "55" : "#333"}`,
                          fontWeight: isActive ? 600 : 400,
                          transition: "all 0.15s",
                        }}
                      >
                        {m.name}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Unwatched Videos Filter */}
            {climbs.some(c => c.video_url) && (
              <div style={{ marginTop: "12px" }}>
                <div style={{ fontSize: "9px", color: "#555", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Video status
                </div>
                <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                  <span
                    onClick={() => setFilterUnwatched(!filterUnwatched)}
                    style={{
                      padding: "3px 10px",
                      borderRadius: "4px",
                      fontSize: "11px",
                      cursor: "pointer",
                      background: filterUnwatched ? "#8b9dc333" : "#2a2a2e",
                      color: filterUnwatched ? "#8b9dc3" : "#666",
                      border: `1px solid ${filterUnwatched ? "#8b9dc355" : "#333"}`,
                      fontWeight: filterUnwatched ? 600 : 400,
                      transition: "all 0.15s",
                    }}
                  >
                    📹 Unwatched only
                  </span>
                </div>
              </div>
            )}

            {/* Active filter count */}
            {(filterStyles.length > 0 || filterMember !== "All" || filterUnwatched || filterGrade !== "All") && (
              <div style={{
                marginTop: "8px",
                fontSize: "10px",
                color: "#7eb5a6",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}>
                <span>Showing {sortedFiltered.length} of {climbs.length} climbs</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sector Columns Grid Layout */}
      <div style={{ padding: "20px", overflowX: "auto" }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "16px",
          alignItems: "start",
        }}>
          {/* Existing Sector Columns */}
          {sectorsWithColors.map(sector => {
            const sectorClimbs = climbs.filter(c => c.sector_id === sector.id);
            const filteredSectorClimbs = sectorClimbs.filter(c => {
              if (filterStyles.length > 0) {
                const climbStyleIds = c.style_ids || [];
                if (styleFilterMode === "any") {
                  if (!filterStyles.some(s => climbStyleIds.includes(s))) return false;
                } else {
                  if (!filterStyles.every(s => climbStyleIds.includes(s))) return false;
                }
              }
              if (filterMember !== "All") {
                const memberInterested = c.interested?.some(i => i.user_id === filterMember);
                if (!memberInterested) return false;
              }
              if (filterUnwatched && c.video_url) {
                // Only show climbs with videos that haven't been watched
                if (watchedClimbs.has(c.id)) return false;
              }
              if (filterGrade !== "All" && gradeFilterMethod) {
                const climbGradeIndex = GRADES.indexOf(c.grade);
                const filterGradeIndex = GRADES.indexOf(filterGrade);
                if (gradeFilterMethod === "gte" && climbGradeIndex < filterGradeIndex) return false;
                if (gradeFilterMethod === "eq" && climbGradeIndex !== filterGradeIndex) return false;
                if (gradeFilterMethod === "lte" && climbGradeIndex > filterGradeIndex) return false;
              }
              return true;
            });

            return (
              <div key={sector.id} style={{
                background: "#1c1c1e",
                borderRadius: "8px",
                border: `2px solid ${sector.colour}`,
                padding: "12px",
                minHeight: "200px",
                boxShadow: `0 0 0 1px ${sector.colour}22`,
              }}>
                {/* Sector Header */}
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "12px",
                  paddingBottom: "8px",
                  borderBottom: `2px solid ${sector.colour}22`,
                }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: "16px",
                    fontWeight: 600,
                    color: sector.colour,
                    fontFamily: "'DM Serif Display', Georgia, serif",
                  }}>
                    {sector.name}
                  </h3>
                  <span style={{ fontSize: "11px", color: "#666" }}>
                    {filteredSectorClimbs.length} climb{filteredSectorClimbs.length !== 1 ? "s" : ""}
                  </span>
                </div>

                {/* Add Climb Button */}
                <button
                  onClick={() => { setNewClimb(p => ({ ...p, sector_id: sector.id })); setModal("climb"); }}
                  style={{
                    ...btnSecondary,
                    width: "100%",
                    marginBottom: "8px",
                    fontSize: "11px",
                    padding: "6px",
                  }}>
                  + Add Climb
                </button>

                {/* Climbs in this sector */}
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {filteredSectorClimbs.map(c => (
                    <ClimbCard
                      key={c.id}
                      climb={c}
                      sectors={sectorsWithColors}
                      styles={styles}
                      members={members}
                      currentUserId={currentUser?.id}
                      onUpdate={refreshData}
                      onDelete={deleteClimb}
                      onPlayVideo={(url, name, climbId) => setVideoState({ url, name, climbId })}
                      onRecommend={(climb) => {
                        setRecommendClimb(climb);
                        setModal("recommend");
                      }}
                    />
                  ))}
                  {filteredSectorClimbs.length === 0 && (
                    <p style={{ fontSize: "11px", color: "#444", textAlign: "center", padding: "20px 0", fontStyle: "italic" }}>
                      No climbs yet
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {sectors.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <p style={{ fontSize: "16px", color: "#666", fontFamily: "'DM Serif Display', Georgia, serif", marginBottom: "8px" }}>
              No sectors yet
            </p>
            <p style={{ fontSize: "12px", color: "#444", marginBottom: "20px" }}>
              Click "+ Sector" above to add your first climbing sector
            </p>
          </div>
        )}
      </div>

      {videoState && (
        <VideoPanel
          url={videoState.url}
          climbName={videoState.name}
          climbId={videoState.climbId}
          userId={currentUser?.id}
          onClose={() => setVideoState(null)}
          onWatched={(climbId) => {
            setWatchedClimbs(prev => new Set([...prev, climbId]));
          }}
        />
      )}

      {modal === "climb" && (
        <Modal onClose={() => setModal(null)}>
          <ModalTitle>Add Problem</ModalTitle>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <input value={newClimb.name} onChange={e => setNewClimb({ ...newClimb, name: e.target.value })}
              placeholder="Problem name" style={inputStyle} autoFocus />
            <div style={{ display: "flex", gap: "6px" }}>
              <select value={newClimb.grade} onChange={e => setNewClimb({ ...newClimb, grade: e.target.value })}
                style={{ ...inputStyle, flex: 1 }}>
                {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
              <select value={newClimb.sector_id} onChange={e => setNewClimb({ ...newClimb, sector_id: e.target.value })}
                style={{ ...inputStyle, flex: 1 }}>
                {sectors.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <input value={newClimb.video_url || ""} onChange={e => setNewClimb({ ...newClimb, video_url: e.target.value })}
              placeholder="YouTube or Instagram link (optional)" style={inputStyle} />
            {styles.length > 0 && (
              <div>
                <div style={{ fontSize: "10px", color: "#888", marginBottom: "4px", textTransform: "uppercase" }}>Styles</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                  {styles.map(s => {
                    const active = (newClimb.style_ids || []).includes(s.id);
                    return (
                      <span key={s.id} onClick={() => {
                        const ids = active ? newClimb.style_ids.filter(x => x !== s.id) : [...(newClimb.style_ids || []), s.id];
                        setNewClimb({ ...newClimb, style_ids: ids });
                      }}
                        style={{
                          padding: "2px 8px", borderRadius: "3px", fontSize: "10px", cursor: "pointer",
                          background: active ? `${s.colour}33` : "#2a2a2e",
                          color: active ? s.colour : "#666",
                          border: `1px solid ${active ? s.colour + "66" : "#333"}`,
                        }}>
                        {s.name}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
            <textarea value={newClimb.notes} onChange={e => setNewClimb({ ...newClimb, notes: e.target.value })}
              placeholder="Notes, beta, conditions..."
              style={{ ...inputStyle, minHeight: "50px", resize: "vertical", fontFamily: "inherit" }} />
            <div style={{ display: "flex", gap: "6px", justifyContent: "flex-end" }}>
              <button onClick={() => setModal(null)} style={btnSecondary}>Cancel</button>
              <button onClick={addClimbFn} style={btnPrimary}>Add</button>
            </div>
          </div>
        </Modal>
      )}

      {duplicateClimb && (
        <Modal onClose={() => setDuplicateClimb(null)}>
          <ModalTitle>Duplicate Climb Found</ModalTitle>
          <div style={{ marginBottom: "16px" }}>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "12px" }}>
              A climb with a similar {duplicateClimb.matchType === 'name' ? 'name' : 'video'} already exists:
            </div>
            <div style={{
              background: "#2a2a2e",
              padding: "12px",
              borderRadius: "4px",
              border: "1px solid #3a3a3e",
              marginBottom: "16px",
            }}>
              <div style={{ fontWeight: 600, fontSize: "14px", color: "#e8e6e3", marginBottom: "6px" }}>
                {duplicateClimb.climb.name}
              </div>
              <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>
                Grade: {duplicateClimb.climb.grade} • {sectors.find(s => s.id === duplicateClimb.climb.sector_id)?.name || 'Unknown Sector'}
              </div>
              {duplicateClimb.climb.video_url && (
                <div style={{ fontSize: "11px", color: "#7eb5a6" }}>
                  Has video
                </div>
              )}
            </div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "12px" }}>
              Would you like to add your interest to the existing climb instead?
            </div>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button onClick={handleCreateAnyway} style={btnSecondary}>
                Create New Anyway
              </button>
              <button onClick={handleAddToExisting} style={btnPrimary}>
                Add to Existing
              </button>
            </div>
          </div>
        </Modal>
      )}

      {modal === "share" && currentTrip && (
        <Modal onClose={() => setModal(null)}>
          <ModalTitle>Share Trip</ModalTitle>
          <div style={{ marginBottom: "16px" }}>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "8px" }}>
              Share this link with your climbing partners to invite them to the trip:
            </div>
            <div style={{
              background: "#2a2a2e",
              padding: "12px",
              borderRadius: "4px",
              fontSize: "13px",
              wordBreak: "break-all",
              fontFamily: "monospace",
              color: "#e8a87c",
              marginBottom: "8px",
            }}>
              {window.location.origin}/invite/{currentTrip.invite_code}
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/invite/${currentTrip.invite_code}`);
                alert('Invite link copied to clipboard!');
              }}
              style={{ ...btnPrimary, width: "100%" }}>
              📋 Copy Invite Link
            </button>
          </div>

          <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #2a2a2e" }}>
            <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px" }}>
              Trip Members ({members.filter(m => m.name).length})
            </div>
            {members.filter(m => m.name).map(m => {
              const color = memberColor(m.name);
              const isCreator = currentTrip.created_by === m.id;
              return (
                <div key={m.id} style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "6px 8px",
                  borderRadius: "4px",
                  background: "#2a2a2e",
                  marginBottom: "4px",
                }}>
                  <span style={{ fontSize: "13px", color: "#e8e6e3" }}>
                    {m.name}
                  </span>
                  <span style={{ fontSize: "10px", color: "#999" }}>
                    {isCreator ? "👑 Owner" : "Member"}
                  </span>
                </div>
              );
            })}
          </div>
        </Modal>
      )}

      {modal === "addSector" && (
        <Modal onClose={() => setModal(null)}>
          <ModalTitle>Add Sector</ModalTitle>
          <div style={{ display: "flex", gap: "6px" }}>
            <input value={newSector.name} onChange={e => setNewSector({ ...newSector, name: e.target.value })}
              placeholder="Sector name" style={{ ...inputStyle, flex: 1 }}
              onKeyDown={e => { if (e.key === "Enter") { addSector(); setModal(null); } }} autoFocus />
            <button onClick={() => { addSector(); setModal(null); }} style={btnPrimary}>Add</button>
          </div>
        </Modal>
      )}

      {modal === "member" && (
        <Modal onClose={() => setModal(null)}>
          <ModalTitle>Add Climber</ModalTitle>
          <div style={{ display: "flex", gap: "6px" }}>
            <input value={newMemberName} onChange={e => setNewMemberName(e.target.value)}
              placeholder="Name" style={{ ...inputStyle, flex: 1 }}
              onKeyDown={e => { if (e.key === "Enter") { addMemberFn(); setModal(null); } }} autoFocus />
            <button onClick={() => { addMemberFn(); setModal(null); }} style={btnPrimary}>Add</button>
          </div>
        </Modal>
      )}

      {modal === "styles" && (
        <Modal onClose={() => setModal(null)}>
          <ModalTitle>Manage Styles</ModalTitle>

          {/* Existing Styles */}
          {styles.length > 0 && (
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "11px", color: "#888", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Your Styles
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                {styles.map(s => (
                  <div key={s.id} style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    background: "#2a2a2e",
                    borderRadius: "4px",
                    border: `1px solid ${s.colour}33`,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <div style={{
                        width: "16px",
                        height: "16px",
                        borderRadius: "3px",
                        background: s.colour,
                        border: "1px solid #1a1a1c",
                      }} />
                      <span style={{ fontSize: "13px", color: "#e8e6e3" }}>{s.name}</span>
                    </div>
                    <button
                      onClick={() => removeStyle(s.id)}
                      style={{
                        padding: "2px 8px",
                        fontSize: "11px",
                        background: "transparent",
                        border: "1px solid #3a3a3e",
                        borderRadius: "3px",
                        color: "#d4726a",
                        cursor: "pointer",
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add New Style */}
          <div style={{ paddingTop: styles.length > 0 ? "16px" : "0", borderTop: styles.length > 0 ? "1px solid #2a2a2e" : "none" }}>
            <div style={{ fontSize: "11px", color: "#888", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Add New Style
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <input
                value={newStyle.name}
                onChange={e => setNewStyle({ ...newStyle, name: e.target.value })}
                placeholder="Style name (e.g., Crimpy, Slab, Overhang)"
                style={inputStyle}
                autoFocus
                onKeyDown={e => {
                  if (e.key === "Enter" && newStyle.name.trim()) {
                    addStyleFn();
                  }
                }}
              />
              <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <span style={{ fontSize: "11px", color: "#999" }}>Color:</span>
                <input
                  type="color"
                  value={newStyle.colour}
                  onChange={e => setNewStyle({ ...newStyle, colour: e.target.value })}
                  style={{
                    width: "50px",
                    height: "30px",
                    border: "1px solid #3a3a3e",
                    borderRadius: "4px",
                    background: "transparent",
                    cursor: "pointer",
                  }}
                />
                <button
                  onClick={() => {
                    if (newStyle.name.trim()) {
                      addStyleFn();
                    }
                  }}
                  disabled={!newStyle.name.trim()}
                  style={{
                    ...btnPrimary,
                    flex: 1,
                    opacity: newStyle.name.trim() ? 1 : 0.5,
                  }}
                >
                  Add Style
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {modal === "recommend" && recommendClimb && (
        <Modal onClose={() => { setModal(null); setRecommendClimb(null); setRecommendTo(""); setRecommendReason(""); }}>
          <ModalTitle>Recommend Climb</ModalTitle>
          <div style={{ marginBottom: "12px", padding: "8px", background: "#2a2a2e", borderRadius: "4px" }}>
            <div style={{ fontSize: "14px", fontWeight: 600 }}>{recommendClimb.name}</div>
            <div style={{ fontSize: "11px", color: "#999" }}>{recommendClimb.grade}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <div>
              <label style={{ display: "block", fontSize: "11px", color: "#999", marginBottom: "4px" }}>
                Recommend to:
              </label>
              <select
                value={recommendTo}
                onChange={e => setRecommendTo(e.target.value)}
                style={inputStyle}
                autoFocus
              >
                <option value="">-- Select member --</option>
                {members.filter(m => m && m.name && m.id !== currentUser?.id).map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: "11px", color: "#999", marginBottom: "4px" }}>
                Reason (optional):
              </label>
              <textarea
                value={recommendReason}
                onChange={e => setRecommendReason(e.target.value)}
                placeholder="Why would they like this?"
                style={{ ...inputStyle, minHeight: "60px", resize: "vertical", fontFamily: "inherit" }}
              />
            </div>
            <div style={{ display: "flex", gap: "6px", justifyContent: "flex-end" }}>
              <button onClick={() => { setModal(null); setRecommendClimb(null); setRecommendTo(""); setRecommendReason(""); }} style={btnSecondary}>
                Cancel
              </button>
              <button onClick={handleRecommend} disabled={!recommendTo} style={btnPrimary}>
                Send Recommendation
              </button>
            </div>
          </div>
        </Modal>
      )}

      {modal === "queue" && (
        <Modal onClose={() => setModal(null)}>
          <ModalTitle>Your Recommendation Queue</ModalTitle>
          {suggestions.length === 0 ? (
            <div style={{ textAlign: "center", padding: "20px", color: "#666" }}>
              No pending recommendations
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {suggestions.map(sugg => {
                const climb = climbs.find(c => c.id === sugg.climb_id);
                const hasVideo = climb && climb.video_url && getYouTubeId(climb.video_url);
                return (
                  <div key={sugg.id} style={{
                    background: "#2a2a2e",
                    border: "1px solid #3a3a3e",
                    borderRadius: "6px",
                    padding: "12px",
                  }}>
                    <div style={{ marginBottom: "8px" }}>
                      <div style={{ fontSize: "14px", fontWeight: 600, color: "#e8e6e3" }}>
                        {sugg.climb_name}
                      </div>
                      <div style={{ fontSize: "11px", color: "#999" }}>
                        {sugg.grade} · {sugg.sector_name}
                      </div>
                      <div style={{ fontSize: "11px", color: "#7eb5a6", marginTop: "4px" }}>
                        Recommended by {sugg.suggested_by_name}
                      </div>
                    </div>
                    {sugg.reason && (
                      <div style={{
                        fontSize: "12px",
                        color: "#ccc",
                        fontStyle: "italic",
                        marginBottom: "8px",
                        padding: "6px 8px",
                        background: "#1c1c1e",
                        borderRadius: "4px",
                      }}>
                        "{sugg.reason}"
                      </div>
                    )}
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                      {hasVideo && (
                        <button
                          onClick={() => {
                            setVideoState({ url: climb.video_url, name: sugg.climb_name, climbId: sugg.climb_id });
                          }}
                          style={{
                            ...btnSecondary,
                            fontSize: "11px",
                            padding: "4px 12px",
                            color: "#d4726a",
                            borderColor: "#d4726a44"
                          }}
                        >
                          ▶ Watch Video
                        </button>
                      )}
                      <button
                        onClick={() => handleAcceptSuggestion(sugg)}
                        style={{ ...btnPrimary, fontSize: "11px", padding: "4px 12px" }}
                      >
                        ✓ Add to Keen List
                      </button>
                      <button
                        onClick={() => handleDismissSuggestion(sugg)}
                        style={{ ...btnSecondary, fontSize: "11px", padding: "4px 12px" }}
                      >
                        ✕ Dismiss
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Modal>
      )}

      {modal === "settings" && (
        <Modal onClose={() => setModal(null)}>
          <ModalTitle>Trip Settings</ModalTitle>

          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

            {/* Trip Name Section - Creator Only */}
            {currentTrip && currentUser && currentTrip.created_by === currentUser.id && (
              <div style={{
                padding: "12px",
                background: "#2a2a2e",
                borderRadius: "6px",
                border: "1px solid #3a3a3e",
              }}>
                <div style={{ fontSize: "11px", color: "#888", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Trip Name
                </div>
                {editingTripName ? (
                  <div style={{ display: "flex", gap: "6px" }}>
                    <input
                      value={tempTripName}
                      onChange={e => setTempTripName(e.target.value)}
                      style={{ ...inputStyle, flex: 1 }}
                      onKeyDown={e => {
                        if (e.key === "Enter") saveTripName();
                        if (e.key === "Escape") {
                          setEditingTripName(false);
                          setTempTripName(tripName);
                        }
                      }}
                      autoFocus
                    />
                    <button onClick={saveTripName} style={btnPrimary}>Save</button>
                    <button onClick={() => {
                      setEditingTripName(false);
                      setTempTripName(tripName);
                    }} style={btnSecondary}>Cancel</button>
                  </div>
                ) : (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: "14px", color: "#e8e6e3", fontWeight: 600 }}>
                      {tripName}
                    </div>
                    <button
                      onClick={() => {
                        setEditingTripName(true);
                        setTempTripName(tripName);
                      }}
                      style={{
                        padding: "4px 10px",
                        background: "transparent",
                        border: "1px solid #3a3a3e",
                        borderRadius: "4px",
                        color: "#999",
                        fontSize: "11px",
                        cursor: "pointer",
                      }}
                    >
                      ✎ Rename
                    </button>
                  </div>
                )}
                <div style={{ fontSize: "11px", color: "#666", marginTop: "6px" }}>
                  {members.length} member{members.length !== 1 ? 's' : ''} · {climbs.length} climb{climbs.length !== 1 ? 's' : ''}
                </div>
              </div>
            )}

            {/* Trip Name Section - Non-Creator (Read-only) */}
            {currentTrip && currentUser && currentTrip.created_by !== currentUser.id && (
              <div style={{
                padding: "12px",
                background: "#2a2a2e",
                borderRadius: "6px",
                border: "1px solid #3a3a3e",
              }}>
                <div style={{ fontSize: "11px", color: "#888", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Current Trip
                </div>
                <div style={{ fontSize: "14px", color: "#e8e6e3", fontWeight: 600 }}>
                  {tripName}
                </div>
                <div style={{ fontSize: "11px", color: "#666", marginTop: "4px" }}>
                  {members.length} member{members.length !== 1 ? 's' : ''} · {climbs.length} climb{climbs.length !== 1 ? 's' : ''}
                </div>
              </div>
            )}

            {/* Member Management Section - Creator Only */}
            {currentTrip && currentUser && currentTrip.created_by === currentUser.id && (
              <div style={{
                padding: "12px",
                background: "#2a2a2e",
                borderRadius: "6px",
                border: "1px solid #3a3a3e",
              }}>
                <div style={{ fontSize: "11px", color: "#888", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Manage Members
                </div>
                {members.filter(m => m.name).map(m => {
                  const isCreator = currentTrip.created_by === m.id;
                  const isCurrentUser = currentUser.id === m.id;
                  return (
                    <div key={m.id} style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px",
                      borderRadius: "4px",
                      background: "#1c1c1e",
                      marginBottom: "6px",
                      border: "1px solid #2a2a2e",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "13px", color: "#e8e6e3" }}>
                          {m.name}
                        </span>
                        {isCreator && (
                          <span style={{
                            fontSize: "10px",
                            color: "#e8a87c",
                            background: "#e8a87c22",
                            padding: "2px 6px",
                            borderRadius: "3px",
                            fontWeight: 600,
                          }}>
                            👑 CREATOR
                          </span>
                        )}
                        {isCurrentUser && !isCreator && (
                          <span style={{ fontSize: "10px", color: "#7eb5a6" }}>
                            (you)
                          </span>
                        )}
                      </div>
                      {!isCreator && (
                        <button
                          onClick={() => removeMemberAsAdmin(m.id)}
                          style={{
                            padding: "4px 10px",
                            background: "transparent",
                            border: "1px solid #d4726a",
                            borderRadius: "4px",
                            color: "#d4726a",
                            fontSize: "11px",
                            fontWeight: 500,
                            cursor: "pointer",
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  );
                })}
                <div style={{ fontSize: "10px", color: "#666", marginTop: "8px", fontStyle: "italic" }}>
                  Removing a member will delete their interests and suggestions
                </div>
              </div>
            )}

            {/* Leave Trip Section - All Users */}
            <div style={{
              padding: "12px",
              background: "#1c1c1e",
              borderRadius: "6px",
              border: "1px solid #3a3a3e",
            }}>
              <div style={{ fontSize: "11px", color: "#888", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Your Actions
              </div>
              <button
                onClick={() => {
                  setModal(null);
                  leaveTrip();
                }}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  background: "transparent",
                  border: "1px solid #d4726a",
                  borderRadius: "4px",
                  color: "#d4726a",
                  fontSize: "12px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Leave Trip
              </button>
              <div style={{ fontSize: "10px", color: "#666", marginTop: "6px", fontStyle: "italic" }}>
                You can rejoin anytime using the invite link
              </div>
            </div>

            {/* Delete Trip Section - Creator Only */}
            {currentTrip && currentUser && currentTrip.created_by === currentUser.id && (
              <div style={{
                padding: "12px",
                background: "#1c1c1e",
                borderRadius: "6px",
                border: "1px solid #d4726a33",
              }}>
                <div style={{ fontSize: "11px", color: "#d4726a", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Danger Zone
                </div>
                <button
                  onClick={deleteTrip}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    background: "#d4726a",
                    border: "none",
                    borderRadius: "4px",
                    color: "#1a1a1c",
                    fontSize: "12px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Delete Trip Permanently
                </button>
                <div style={{ fontSize: "10px", color: "#999", marginTop: "6px", fontStyle: "italic" }}>
                  This will delete the trip and all its data. This cannot be undone.
                </div>
              </div>
            )}

            {/* User Info Section - All Users */}
            <div style={{
              padding: "12px",
              background: "#2a2a2e",
              borderRadius: "6px",
              border: "1px solid #3a3a3e",
            }}>
              <div style={{ fontSize: "11px", color: "#888", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Logged in as
              </div>
              <div style={{ fontSize: "13px", color: "#e8e6e3" }}>
                {currentUser?.name}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
